export const defaultThemeColors = {
  primaryColor: '#2c2c2c',
  secondaryColor: '#f9f9f9',
  sectionAltColor: '#f8f9fa',
  cardColor: '#ffffff',
  inputBackgroundColor: '#ffffff',
  accentColor: '#c0a080',
  accentHoverColor: '#b39173',
  buttonTextColor: '#f9f9f9',
  textColor: '#333333',
  lightText: '#f9f9f9',
  darkText: '#2c2c2c',
  mutedTextColor: '#777777',
  borderColor: '#e5e5e5',
  errorColor: '#d32f2f',
  successColor: '#388e3c'
};

export const themeColorGroups = [
  {
    title: 'Layout & backgrounds',
    fields: [
      {
        key: 'primaryColor',
        label: 'Primary',
        description: 'Dark structural color for navigation, footer, and hero backgrounds — not page titles.',
        locations: ['Navbar (all pages)', 'Footer (all pages)', 'Home hero overlay', 'Page header bands']
      },
      {
        key: 'secondaryColor',
        label: 'Page background',
        description: 'Default page fill behind most content sections.',
        locations: ['Main page background', 'Home welcome area', 'Contact & lesson cards', 'About / Gallery pages']
      },
      {
        key: 'sectionAltColor',
        label: 'Alternating sections',
        description: 'Slightly different background for alternating bands.',
        locations: ['Home · Upcoming Events', 'Bookings / Lessons gray bands', 'About alternating sections']
      }
    ]
  },
  {
    title: 'Cards & forms',
    fields: [
      {
        key: 'cardColor',
        label: 'Card background',
        description: 'Raised panels that sit on top of the page background.',
        locations: ['Home feature cards', 'Booking form card', 'Gallery video/audio cards']
      },
      {
        key: 'inputBackgroundColor',
        label: 'Input background',
        description: 'Background inside text fields before the visitor types.',
        locations: ['Contact form fields', 'Bookings form', 'Lessons request form']
      },
      {
        key: 'borderColor',
        label: 'Borders',
        description: 'Lines around inputs, cards, and dividers.',
        locations: ['Form field outlines', 'Card edges', 'Section dividers']
      }
    ]
  },
  {
    title: 'Accent & buttons',
    fields: [
      {
        key: 'accentColor',
        label: 'Accent',
        description: 'Main brand highlight for actions and links.',
        locations: ['Book / Lesson buttons', 'Footer social icons', 'Section title underlines', 'About tag pills']
      },
      {
        key: 'accentHoverColor',
        label: 'Accent hover',
        description: 'Color when hovering primary buttons and some links.',
        locations: ['Book a Performance button (hover)', 'Gallery controls (hover)']
      },
      {
        key: 'buttonTextColor',
        label: 'Button text',
        description: 'Text color on filled accent buttons.',
        locations: ['Hero CTA buttons', 'Primary buttons site-wide', 'About instrument tags']
      }
    ]
  },
  {
    title: 'Text',
    fields: [
      {
        key: 'textColor',
        label: 'Body text',
        description: 'Standard paragraph text on light backgrounds.',
        locations: ['Home welcome copy', 'About bio', 'Contact descriptions', 'Card descriptions']
      },
      {
        key: 'darkText',
        label: 'Headings',
        description: 'Section titles and card headings on light backgrounds.',
        locations: ['Section titles (e.g. Welcome, Featured)', 'Feature card titles', 'Form section headings']
      },
      {
        key: 'lightText',
        label: 'Light text',
        description: 'Text placed on dark backgrounds.',
        locations: ['Navbar links', 'Footer text', 'Home hero name + subtitle']
      },
      {
        key: 'mutedTextColor',
        label: 'Muted text',
        description: 'Softer supporting text below main headings.',
        locations: ['Page subtitles under headers', 'Home section subtitles', 'Gallery helper text']
      }
    ]
  },
  {
    title: 'Feedback',
    fields: [
      {
        key: 'errorColor',
        label: 'Error',
        description: 'Validation and submission error messaging.',
        locations: ['Contact form errors', 'Bookings / Lessons form errors']
      },
      {
        key: 'successColor',
        label: 'Success',
        description: 'Confirmation after a form sends successfully.',
        locations: ['Contact thank-you message', 'Bookings / Lessons success notice']
      }
    ]
  }
];

export const themeColorFields = themeColorGroups.flatMap((group) => group.fields);

const THEME_VAR_MAP = {
  primaryColor: '--primary-color',
  secondaryColor: '--secondary-color',
  sectionAltColor: '--section-alt-color',
  cardColor: '--card-color',
  inputBackgroundColor: '--input-background-color',
  accentColor: '--accent-color',
  accentHoverColor: '--accent-hover-color',
  buttonTextColor: '--button-text-color',
  textColor: '--text-color',
  lightText: '--light-text',
  darkText: '--dark-text',
  mutedTextColor: '--muted-text-color',
  borderColor: '--border-color',
  errorColor: '--error-color',
  successColor: '--success-color'
};

const DERIVED_THEME_VARS = [
  '--bg-light',
  '--text-dark',
  '--accent-dark',
  '--accent-light',
  '--accent-color-light',
  '--accent-color-rgb',
  '--primary-color-rgb',
  '--success-color-rgb',
  '--error-color-rgb',
  '--muted-text',
  '--text-muted'
];

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const normalizeHex = (value) => {
  if (!isValidHexColor(value)) return '';
  const trimmed = value.trim().toLowerCase();
  if (trimmed.length === 4) {
    return `#${trimmed.slice(1).split('').map((char) => char + char).join('')}`;
  }
  return trimmed;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex).slice(1);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
};

const mixHex = (hex, mixWith, weight = 0.5) => {
  const source = hexToRgb(hex);
  const target = hexToRgb(mixWith);
  const blend = (from, to) => Math.round(from * (1 - weight) + to * weight);
  const r = blend(source.r, target.r);
  const g = blend(source.g, target.g);
  const b = blend(source.b, target.b);
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

export const isValidHexColor = (value) =>
  typeof value === 'string' && HEX_COLOR_RE.test(value.trim());

export const mergeThemeColors = (stored) => {
  const merged = { ...defaultThemeColors };

  if (!stored || typeof stored !== 'object') {
    return merged;
  }

  Object.keys(defaultThemeColors).forEach((key) => {
    const value = stored[key];
    if (isValidHexColor(value)) {
      merged[key] = value.trim();
    }
  });

  return merged;
};

export const isDefaultTheme = (colors = {}) => {
  const merged = mergeThemeColors(colors);
  return Object.keys(defaultThemeColors).every(
    (key) => normalizeHex(merged[key]) === normalizeHex(defaultThemeColors[key])
  );
};

export const themeColorsToCssVars = (colors = {}) => {
  const merged = mergeThemeColors(colors);
  const vars = {};

  Object.entries(THEME_VAR_MAP).forEach(([key, cssVar]) => {
    vars[cssVar] = merged[key];
  });

  const accentRgb = hexToRgb(merged.accentColor);
  const primaryRgb = hexToRgb(merged.primaryColor);
  const successRgb = hexToRgb(merged.successColor);
  const errorRgb = hexToRgb(merged.errorColor);
  const accentLight = mixHex(merged.accentColor, '#ffffff', 0.55);

  vars['--bg-light'] = merged.sectionAltColor;
  vars['--text-dark'] = merged.darkText;
  vars['--accent-dark'] = merged.accentHoverColor;
  vars['--accent-light'] = accentLight;
  vars['--accent-color-light'] = accentLight;
  vars['--accent-color-rgb'] = `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`;
  vars['--primary-color-rgb'] = `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`;
  vars['--success-color-rgb'] = `${successRgb.r}, ${successRgb.g}, ${successRgb.b}`;
  vars['--error-color-rgb'] = `${errorRgb.r}, ${errorRgb.g}, ${errorRgb.b}`;
  vars['--muted-text'] = merged.mutedTextColor;
  vars['--text-muted'] = merged.mutedTextColor;

  return vars;
};

export const clearThemeColorOverrides = (target = document.documentElement) => {
  if (!target) return;

  [...Object.values(THEME_VAR_MAP), ...DERIVED_THEME_VARS].forEach((cssVar) => {
    target.style.removeProperty(cssVar);
  });
};

export const applyThemeColors = (colors = {}, target = document.documentElement) => {
  if (!target) return;

  if (isDefaultTheme(colors)) {
    clearThemeColorOverrides(target);
    return;
  }

  const vars = themeColorsToCssVars(colors);
  Object.entries(vars).forEach(([cssVar, value]) => {
    target.style.setProperty(cssVar, value);
  });
};
